import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { getAnswer1 } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/__internal__/LoggedOutCategoryPageC1Answer1Content";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization";
import { AccordionItem } from "@faire/web/slate/Accordion";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ICategory } from "@faire/web-api--source/indigofair/data/ICategory";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import { History } from "history";
import React from "react";

// All StrictLocalMsg below have been explicitly discussed offline regarding the usage of the placeholders and their
// usage in translation will be checked thoroughly
const getQuestion1 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      defaultMessage:
        "What {categoryName} products can I buy on Faire at wholesale prices for my store?",
      description: {
        text: 'first question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getQuestion2 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      defaultMessage:
        "How can I buy {categoryName} products at wholesale prices for my store?",
      description: {
        text: 'second question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getAnswer2 = (history: History, category: ICategory) => (
  <Typography>
    <StrictLocalMsg
      defaultMessage="Whether you’re buying products for a brick-and-mortar store, online shop, or pop-up shop, you can shop {categoryName} products at wholesale prices on Faire—<SignUpLinkFormatting>sign up for free</SignUpLinkFormatting>. When you join as a retailer, you’ll unlock wholesale prices with thousands of independent brands. You’ll also get exclusive shopping benefits, including free returns on all opening orders and the option to buy now, pay later. <LearnMoreLinkFormatting>Learn more</LearnMoreLinkFormatting>."
      description={{
        text: 'second answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      }}
      values={{
        ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
          categoryName: category.name,
        }),
        SignUpLinkFormatting: (chunks: React.ReactNode) => (
          <Button
            variant="tertiary"
            onClick={() =>
              popSignUp(history, SignUpType.GENERIC, () => null, {
                element: SignUpElement.BELOW_FOLD,
                sourceId: "category_page_faq_answer_2",
              })
            }
          >
            {chunks}
          </Button>
        ),
        LearnMoreLinkFormatting: (chunks: React.ReactNode) => (
          <Button
            variant="tertiary"
            linkTo={supportArticle("360015892572")}
            target="_blank"
          >
            {chunks}
          </Button>
        ),
      }}
    />
  </Typography>
);

const getQuestion3 = (
  category: ICategory,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      defaultMessage:
        "How do I find different {categoryName} brands that sell wholesale to retailers like me?",
      description: {
        text: 'third question in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      categoryName: category.name,
    })
  );

const getAnswer3 = (category: ICategory) => (
  <Typography>
    <StrictLocalMsg
      defaultMessage="Faire’s platform consolidates brands that sell {categoryName} products at wholesale prices to retailers onto a single site, making it possible to buy from independent brands you might not have come across yet. Just a quick browse through Faire’s {categoryName} listings can offer you a range of styles that are perfect for your store."
      description={{
        text: 'third answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
      }}
      values={DEPRECATED_backwardsCompatibleValues({
        categoryName: category.name,
      })}
    />
  </Typography>
);

export const getC1CategoryFAQs = (
  history: History,
  category: ICategory,
  subCategories: ICategory[] | undefined,
  strictLocalize: StrictLocalizeFunction
): AccordionItem[] => {
  const faqItems = [
    {
      question: getQuestion1(category, strictLocalize),
      answer: getAnswer1(history, category, subCategories),
    },
    {
      question: getQuestion2(category, strictLocalize),
      answer: getAnswer2(history, category),
    },
    {
      question: getQuestion3(category, strictLocalize),
      answer: getAnswer3(category),
    },
  ];
  return faqItems.filter((faqItem) => faqItem.question && faqItem.answer);
};
